'use client'

import { useRef, useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface AnimatedEyeProps {
  isOpen: boolean      // password hidden = eye open (watching), password visible = eye closed
  onToggle: () => void // click handler
}

const IRIS_COLOR = '#544ae8'
const PUPIL_COLOR = '#1a1625'
const SCLERA_COLOR = '#f0eef8'
const MAX_PUPIL_OFFSET = 4    // max px the pupil drifts from center
const ATTENTION_RADIUS = 200  // px — within this distance the eye "locks on"
const DRIFT_SPEED = 600       // ms — how fast pupil drifts back to center

export default function AnimatedEye({ isOpen, onToggle }: AnimatedEyeProps) {
  const eyeRef = useRef<SVGSVGElement>(null)
  const [pupilOffset, setPupilOffset] = useState({ x: 0, y: 0 })
  const [isNearby, setIsNearby] = useState(false)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!eyeRef.current || !isOpen) return

      const rect = eyeRef.current.getBoundingClientRect()
      const eyeCenterX = rect.left + rect.width / 2
      const eyeCenterY = rect.top + rect.height / 2

      const dx = e.clientX - eyeCenterX
      const dy = e.clientY - eyeCenterY
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < ATTENTION_RADIUS) {
        setIsNearby(true)
        // Normalize and clamp offset
        const angle = Math.atan2(dy, dx)
        const strength = Math.min(distance / ATTENTION_RADIUS, 1)
        const offsetX = Math.cos(angle) * MAX_PUPIL_OFFSET * strength
        const offsetY = Math.sin(angle) * MAX_PUPIL_OFFSET * strength
        setPupilOffset({ x: offsetX, y: offsetY })
      } else {
        setIsNearby(false)
        // Drift back toward center (CSS transition handles the easing)
        setPupilOffset({ x: 0, y: 0 })
      }
    },
    [isOpen],
  )

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    return () => document.removeEventListener('mousemove', handleMouseMove)
  }, [handleMouseMove])

  // Reset pupil when eye closes
  useEffect(() => {
    if (!isOpen) {
      setPupilOffset({ x: 0, y: 0 })
      setIsNearby(false)
    }
  }, [isOpen])

  // Transition duration depends on whether the cursor is nearby
  const pupilTransition = isNearby ? '80ms ease-out' : `${DRIFT_SPEED}ms cubic-bezier(.4,0,.2,1)`

  return (
    <button
      type="button"
      onClick={onToggle}
      tabIndex={-1}
      aria-label={isOpen ? 'Show password' : 'Hide password'}
      className="group relative flex items-center justify-center"
      style={{ width: 28, height: 28 }}
    >
      <svg
        ref={eyeRef}
        viewBox="0 0 28 28"
        width={28}
        height={28}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="overflow-visible"
      >
        <AnimatePresence mode="wait" initial={false}>
          {isOpen ? (
            /* ====== OPEN EYE (watching) ====== */
            <motion.g
              key="open"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* Sclera — almond shape */}
              <motion.path
                d="M2 14 C2 14, 7 6, 14 6 C21 6, 26 14, 26 14 C26 14, 21 22, 14 22 C7 22, 2 14, 2 14Z"
                fill={SCLERA_COLOR}
                stroke="rgba(255,255,255,0.15)"
                strokeWidth={0.8}
                initial={{ scaleY: 0.1 }}
                animate={{ scaleY: 1 }}
                exit={{ scaleY: 0.1 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                style={{ transformOrigin: '14px 14px' }}
              />

              {/* Iris */}
              <circle
                cx={14 + pupilOffset.x}
                cy={14 + pupilOffset.y}
                r={5}
                fill={IRIS_COLOR}
                style={{
                  transition: `cx ${pupilTransition}, cy ${pupilTransition}`,
                }}
              />

              {/* Pupil */}
              <circle
                cx={14 + pupilOffset.x}
                cy={14 + pupilOffset.y}
                r={2.2}
                fill={PUPIL_COLOR}
                style={{
                  transition: `cx ${pupilTransition}, cy ${pupilTransition}`,
                }}
              />

              {/* Pupil highlight */}
              <circle
                cx={14 + pupilOffset.x + 1.2}
                cy={14 + pupilOffset.y - 1.2}
                r={0.9}
                fill="rgba(255,255,255,0.7)"
                style={{
                  transition: `cx ${pupilTransition}, cy ${pupilTransition}`,
                }}
              />

              {/* Upper eyelid line */}
              <path
                d="M2 14 C2 14, 7 6, 14 6 C21 6, 26 14, 26 14"
                fill="none"
                stroke="rgba(255,255,255,0.25)"
                strokeWidth={1}
                strokeLinecap="round"
              />

              {/* Lower eyelid line */}
              <path
                d="M2 14 C2 14, 7 22, 14 22 C21 22, 26 14, 26 14"
                fill="none"
                stroke="rgba(255,255,255,0.15)"
                strokeWidth={0.6}
                strokeLinecap="round"
              />
            </motion.g>
          ) : (
            /* ====== CLOSED EYE (sleeping / password visible) ====== */
            <motion.g
              key="closed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* Squinted sclera — very thin almond */}
              <motion.path
                d="M2 14 C2 14, 7 11, 14 11 C21 11, 26 14, 26 14 C26 14, 21 17, 14 17 C7 17, 2 14, 2 14Z"
                fill={SCLERA_COLOR}
                fillOpacity={0.3}
                stroke="rgba(255,255,255,0.15)"
                strokeWidth={0.8}
                initial={{ scaleY: 2 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                style={{ transformOrigin: '14px 14px' }}
              />

              {/* Closed eyelid curve */}
              <motion.path
                d="M3 14 C3 14, 8 10, 14 10 C20 10, 25 14, 25 14"
                fill="none"
                stroke="rgba(255,255,255,0.4)"
                strokeWidth={1.2}
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              />

              {/* Strike-through line */}
              <motion.line
                x1={5}
                y1={22}
                x2={23}
                y2={6}
                stroke="rgba(255,255,255,0.5)"
                strokeWidth={1.4}
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.25, delay: 0.1, ease: 'easeOut' }}
              />

              {/* Small eyelash marks for "sleeping" feel */}
              <motion.path
                d="M9 17 L8 19.5 M14 17.5 L14 20 M19 17 L20 19.5"
                fill="none"
                stroke="rgba(255,255,255,0.3)"
                strokeWidth={0.8}
                strokeLinecap="round"
                initial={{ opacity: 0, y: -2 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.15 }}
              />
            </motion.g>
          )}
        </AnimatePresence>
      </svg>
    </button>
  )
}
